import { AnimationEvent } from '@angular/animations';
import { ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { listItem } from '@lockton-apps/shared/ui/lui';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import {
  clone,
  compact,
  defaults,
  defaultsDeep,
  filter,
  findIndex,
  first,
  forEach,
  isNil,
  isObject,
  isObjectLike,
  map,
  transform,
  uniq,
  values,
} from 'lodash';
import {
  BehaviorSubject,
  Observable,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { map as rxMap } from 'rxjs/operators';
import { DynamicFormTableRow } from '../dynamic-form-table/dynamic-form-table.component';
import {
  DynamicFormCalcTableCell,
  DynamicFormCalcTableTotal,
} from '../dynamic-form-calc-table/dynamic-form-calc-table.component';

interface InputProps extends FormlyFieldProps {
  tooltip: { text: string };
  distinctBy: string;
  addonLeft: string;
  addonRight: string;
  fields: FormlyFieldConfig[];
  showAll: boolean;
  cache: Cache;
  min: number;
  max: number;
  readonly: boolean;
  print: boolean;
  addAnotherText: string;
  saveText: string;
  cancelText: string;
  saveChangesText: string;
  fieldsClassName?: string;
  startEmpty?: boolean;
  fileDownloadHandler?: (fileId: string, fileName?: string) => Promise<void>;
}

interface Cache {
  toggleList?: CacheConfigToggleList;
  table?: CacheConfigTable;
  calcTable?: CacheConfigCalcTable;
}

interface CacheConfigToggleList {
  distinctBy: string;
  multi: boolean;
  summary: CacheConfigToggleListSummary;
  breakpoint: number;
  visibilityLimit: number;
  totalLabel: string;
}

export interface CacheConfigToggleListSummary {
  label: string;
  addonLeft: string;
  addonRight: string;
  decimalFormat: string;
  omit: string[];
}

interface CacheConfigTable {
  totalLabel: string;
  totalCountFilter: string;
}

interface CacheConfigCalcTable {
  distinctBy?: string;
  cells: Record<string, DynamicFormCalcTableCell>;
  total: DynamicFormCalcTableTotal;
}

export interface DynamicFormCustomEvent {
  name: string;
  value: unknown;
}

export enum DynamicFieldAction {
  Initialize,
  Create,
  Update,
  UpdateCancelled,

  Delete,
}

enum DynamicFieldModes {
  AddAnother,
  Create,
  Update,
}

@Component({
    selector: 'lockton-apps-dynamic-field-type',
    templateUrl: './dynamic-field-type.component.html',
    styleUrls: ['./dynamic-field-type.component.scss'],
    animations: [listItem],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DynamicFieldTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly Infinity: number = Infinity;
  readonly internalOptions = {
    formState: {
      isMulti: true,
      externalAction$: () => this.action$.pipe(takeUntil(this.destroyer$)),
    },
  };
  readonly fields: FormlyFieldConfig[][] = [];
  readonly internalForm: FormArray = new FormArray<any>([]);
  readonly action$: BehaviorSubject<DynamicFieldAction> = new BehaviorSubject(
    DynamicFieldAction.Initialize as DynamicFieldAction
  );
  readonly destroyer$: Subject<void> = new Subject();
  itemInEdit!: number;
  addingAnother: boolean = false;
  cachedUpdateValue: unknown;

  rows$!: Observable<DynamicFormTableRow[]>;

  constructor(
    private readonly viewportScroller: ViewportScroller,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeOptions();
  }

  ngAfterViewInit(): void {
    this.initializeRows();
    this.listenToChanges();
    this.formControl.updateValueAndValidity();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  get min() {
    return this.props.min || 0;
  }

  get isCurrentEmpty() {
    return !isNil(this.itemInEdit)
      ? this.isEmptyValue(this.internalForm.at(this.itemInEdit).value)
      : true;
  }

  get addedItemsCount(): number {
    return this.internalForm.controls.length - +this.addingAnother;
  }

  get canSave(): boolean {
    return (
      ((this.internalForm.length === 1 && this.internalForm.valid) ||
        (this.internalForm.length > 1 && !this.addingAnother) ||
        this.internalForm.at(this.itemInEdit)?.valid) &&
      !this.isCurrentEmpty
    );
  }

  mode: DynamicFieldModes = DynamicFieldModes.AddAnother;

  modes = DynamicFieldModes;

  addAnother(manualTrigger?: boolean): void {
    this.mode = DynamicFieldModes.Create;

    this.internalForm.push(new FormGroup({}), { emitEvent: false });
    this.fields.push(this.cloneFields(this.field.props.fields));
    this.itemInEdit = this.internalForm.length - 1;
    this.addingAnother = true;

    if (manualTrigger) {
      this.action$.next(DynamicFieldAction.Create);
    }
  }

  saveChanges(): void {
    this.mode = DynamicFieldModes.AddAnother;

    this.itemInEdit = undefined as unknown as number;
    this.addingAnother = false;
    this.action$.next(DynamicFieldAction.Create);
  }

  edit(index: number): void {
    this.mode = DynamicFieldModes.Update;

    if (this.addingAnother) {
      this.internalForm.removeAt(this.internalForm.length - 1);
      this.addingAnother = false;
    }

    this.itemInEdit = index;
    this.action$.next(DynamicFieldAction.Update);
  }

  cancelEdit() {
    this.mode = DynamicFieldModes.AddAnother;

    !isNil(this.itemInEdit) &&
      this.internalForm.at(this.itemInEdit).patchValue(this.cachedUpdateValue);
    this.itemInEdit = undefined as unknown as number;
    this.action$.next(DynamicFieldAction.UpdateCancelled);
  }

  delete(index: number): void {
    this.mode = DynamicFieldModes.AddAnother;

    if (this.addingAnother) {
      this.internalForm.removeAt(this.internalForm.length - 1);
      this.addingAnother = false;
    }

    this.itemInEdit = undefined as unknown as number;
    this.internalForm.removeAt(index);
    this.fields.splice(index, 1);

    if (!this.internalForm.length && !(this.props.startEmpty && this.min < 1)) {
      this.addAnother();
    }
    this.action$.next(DynamicFieldAction.Delete);
  }

  onCustomEvent(event: DynamicFormCustomEvent): void {
    if (!this.props.fileDownloadHandler) {
      throw new Error('Download handler is not defined');
    }

    if (typeof event !== 'object') {
      throw new Error('Event must be an object');
    }

    switch (event.name) {
      case 'download': {
        if (
          !(
            isObject(event.value) &&
            'fileName' in event.value &&
            'fileId' in event.value &&
            (typeof event.value.fileId === 'string' ||
              typeof event.value.fileId === 'number') &&
            typeof event.value.fileName === 'string'
          )
        ) {
          throw new Error('Download in incorrect format:');
        }
        this.props.fileDownloadHandler(
          event.value.fileId.toString(),
          event.value.fileName
        );
        break;
      }
      default: {
        throw new Error(`Unknown event: ${event.name}`);
      }
    }
  }

  private initializeForm(): void {
    const validators: ValidatorFn[] = [
      this.invalidAncestorsValidator.bind(this),
      this.minValidator.bind(this),
    ];

    if (this.props.required) {
      validators.push(Validators.required);
    }

    this.formControl.setValidators(validators);

    if (
      !this.field.defaultValue?.length &&
      !(this.props.startEmpty && this.min < 1)
    ) {
      return this.addAnother();
    }

    forEach(this.field.defaultValue, (value) => {
      this.internalForm.push(new FormGroup({}), { emitEvent: false });
      this.fields.push(
        map(this.field.props.fields, (field: FormlyFieldConfig) =>
          defaultsDeep({ defaultValue: value[field.key as string] }, field)
        )
      );
    });
  }

  private initializeOptions(): void {
    this.internalOptions.formState = defaults(
      this.internalOptions.formState,
      this.options.formState
    );
  }

  private initializeRows(): void {
    this.rows$ = this.action$.pipe(
      startWith(null),
      rxMap(() => this.buildTableRows(this.internalForm.value))
    );
  }

  private listenToChanges(): void {
    this.action$
      .pipe(takeUntil(this.destroyer$))
      .subscribe(this.onInternalAction.bind(this));
    this.internalForm.valueChanges
      .pipe(takeUntil(this.destroyer$))
      .subscribe(this.onInternalFormValueChange.bind(this));
    if (this.options.formState.validityCheck$) {
      this.options.formState.validityCheck$
        .pipe(takeUntil(this.destroyer$))
        .subscribe(this.onGlobalValidityCheck.bind(this));
    }
  }

  private onInternalAction(action: DynamicFieldAction): void {
    if (action !== DynamicFieldAction.Initialize) {
      this.formControl.markAsDirty();
    }
  }

  private onInternalFormValueChange(internalModel: unknown[]): void {
    if (!this.filterEmptyValues(internalModel).length) {
      return this.formControl.setValue([]);
    }

    this.formControl.patchValue(internalModel);
    this.form.updateValueAndValidity();
  }

  private onGlobalValidityCheck(): void {
    this.internalForm.markAllAsTouched();

    const firstInvalidFormIndex: number = findIndex(
      this.internalForm.controls,
      { invalid: true }
    );

    if (firstInvalidFormIndex > -1) {
      this.itemInEdit = firstInvalidFormIndex;
    }

    this.cdr.markForCheck();
  }

  public scrollToForm(event: AnimationEvent): void {
    if (event.toState === 'void') {
      this.viewportScroller.setOffset([0, 20]);
      this.viewportScroller.scrollToAnchor(this.field.key as string);
    }
  }

  private cloneFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return map(fields, (field: FormlyFieldConfig) => {
      const clonedField: FormlyFieldConfig = clone(field);

      clonedField.props = field.props;

      return clonedField;
    });
  }

  private buildTableRows(model: any[]): DynamicFormTableRow[] {
    return transform(
      model,
      (rows: DynamicFormTableRow[], item: any, index: number) => {
        if (index !== this.itemInEdit) {
          rows.push({
            item,
            __meta: {
              id: index,
              valid: this.internalForm.at(index).valid,
            },
          });
        }
      },
      []
    );
  }

  private invalidAncestorsValidator(): ValidationErrors | null {
    return this.internalForm.invalid ? { invalidAncestors: true } : null;
  }

  private minValidator(): ValidationErrors | null {
    return this.props.min > this.internalForm.length ? { min: true } : null;
  }

  // TODO refactro below
  private filterEmptyValues(model: unknown[]): unknown[] {
    return filter(model, (item) => {
      return !this.isEmptyValue(item);
    });
  }

  private isEmptyValue(item: unknown): boolean {
    const allValues = values(item);
    let checkedValue;

    if (allValues.length === 1) {
      const value = first(allValues);
      checkedValue = isObjectLike(value) ? compact(values(value)) : [value];
    } else {
      checkedValue = allValues;
    }

    return compact(uniq(checkedValue)).length === 0;
  }
}
